/**
 * Tipos do contrato loopback do Barid (agente local do Wazir). Copia enxuta do
 * pacote @alfasu-br/barid-contract 1.40 (repo alfasu-br/barid) com o que o
 * wazir-web usa; manter os nomes iguais aos do contrato. Todo campo novo e
 * opcional: agente antigo simplesmente nao o envia.
 */

export type BaridAgentAction =
  | 'STATUS'
  | 'PAIR'
  | 'PRINT'
  | 'PRINT_LABEL'
  | 'DISCOVER_PRINTERS'
  | 'TEF_PAYMENT'
  | 'TEF_STATUS'
  | 'TEF_CANCEL'
  | 'TEF_CONFIRM'
  | 'APPLY_TEF_CONFIG';

export interface BaridCommandContext {
  tenantId?: string;
  filialId?: string;
  terminalId?: string;
  /** API do Wazir onde o agente troca o codigo de pareamento. */
  apiUrl?: string;
}

export interface BaridCommandRequest<TPayload = unknown> {
  requestId: string;
  apiVersion: string;
  action: BaridAgentAction;
  timestamp: string;
  context?: BaridCommandContext;
  payload: TPayload;
}

export interface BaridCommandResponse<TData = unknown> {
  requestId: string;
  success: boolean;
  data?: TData;
  error?: { code?: string; message?: string } | string;
  timestamp?: string;
}

export interface BaridMachineIdentity {
  machineId: string;
  hostname?: string;
  localIpAddresses?: string[];
  platform?: string;
  arch?: string;
  osRelease?: string;
}

export interface BaridPairingProfile {
  tenantId?: string;
  tenantName?: string;
  deviceSettingId?: string;
  deviceName?: string;
  filialId?: string;
  filialName?: string;
  pairedAt?: string;
  lastSeenAt?: string;
  agentVersion?: string;
  apiVersion?: string;
  online?: boolean;
}

/** `ILocalStateUnreadable`: config.enc ilegivel no boot (o agente subiu sem pareamento). */
export interface BaridLocalStateUnreadable {
  reason: string;
  backupPath?: string;
  detectedAt: string;
  /** Mensagem pronta (pt-BR) para o operador. */
  message: string;
}

export type BaridOfflineMode = 'ONLINE' | 'DEGRADED' | 'OFFLINE' | 'RESYNCING';

/** Bloco `offline` do /status do Barid. Com a chave OFFLINE_EDGE OFF o agente devolve `enabled:false` (ou omite). */
export interface BaridOfflineStatus {
  enabled: boolean;
  mode: BaridOfflineMode;
  modeSince?: string;
  /** Ops no outbox local ainda nao confirmadas pela nuvem. */
  pending?: number;
  /** Ops em quarentena/DLQ local (precisam de revisao). */
  held?: number;
}

export interface BaridAgentStatus {
  agentVersion: string;
  apiVersion: string;
  environment?: 'production' | 'staging' | 'development';
  online: boolean;
  paired: boolean;
  compatible: boolean;
  simulation: boolean;
  machineIdentity?: BaridMachineIdentity;
  pairingProfile?: BaridPairingProfile;
  cloudPairingRejected?: { code: 'CREDENTIAL_REVOKED' | 'PAIRING_REPLACED'; rejectedAt: string; nextRetryAt?: string };
  /** A nuvem recusou esta maquina (nao e falta de rede); `message` vem pronta para a tela. */
  cloudRefusal?: { reason: 'CREDENTIAL_REFUSED' | 'TERMINAL_SUBSTITUTED'; message: string };
  /**
   * Contrato 1.20: o agente ainda esta (re)carregando o config.enc. Enquanto
   * `true`, `paired` vem `false` mas NAO significa "nao pareado": o agente
   * recusa `/pair` e a tela nao deve oferecer pareamento nem salvar nada nele.
   */
  configLoading?: boolean;
  /** Falha definitiva ao ler o config.enc (arquivo preservado). */
  configLoadError?: string;
  localStateUnreadable?: BaridLocalStateUnreadable;
  /** Contrato 1.30: ticket do agente local (X-Barid-Agent-Ticket). */
  localAgentTicket?: { supported: true; ready: boolean };
  /** Modo offline (base tecnica, specs/wazir/offline/00-base.md). Ausente ou `enabled:false` = comportamento de hoje. */
  offline?: BaridOfflineStatus;
  capabilities?: string[];
}

export interface BaridPrinterDescriptor {
  /** Chave estavel no computador (vira `stableKey` em `barid_printers`). */
  id?: string;
  name: string;
  connection?: 'local' | 'network';
  connectionType?: string;
  endpoint?: string;
  portName?: string;
  host?: string;
  port?: number;
  printType?: string;
  paperType?: string;
  printerType?: string;
  paperSize?: string;
  driverLanguage?: string;
  available?: boolean;
}

export interface BaridDiscoverPrintersResult {
  printers: BaridPrinterDescriptor[];
  simulation?: boolean;
}

export interface BaridPrintPayload {
  title?: string;
  printer?: BaridPrinterDescriptor;
  /**
   * Contrato 1.40: id da impressora VIRTUAL do Wazir (`barid_printers.id`).
   * Nos jobs pela nuvem a API preenche sozinha a partir do `printerId`.
   */
  virtualId?: string;
  action?: string;
  area?: string;
  templateKey?: string;
  copies?: number;
  html: string;
  qrCode?: string;
}

/** ITefDeviceConfig do contrato. */
export interface BaridTefDeviceConfig {
  requestFolder?: string;
  responseFolder?: string;
  environment?: 'homologation' | 'production';
  uiCustomization?: {
    configTefJsonPath?: string;
    logoPath?: string;
    logoBase64?: string;
    highlightColor?: string;
    fontColor1?: string;
    fontColor2?: string;
  };
}

/** Erro do agente local com a mensagem pronta para a tela. */
export class BaridAgentError extends Error {
  constructor(message: string, readonly code?: string, readonly status?: number) {
    super(message);
    this.name = 'BaridAgentError';
  }
}

/**
 * Contrato 1.30.0 — ticket do agente local. Resposta do
 * `POST barid-devices/local-agent-ticket` (wazir-api): JWS opaco para o
 * navegador, validade de 12 h. Vai no header `X-Barid-Agent-Ticket` (com o
 * `x-tenant-id`) e na 1a mensagem do WS `/events`. Nunca logar nem persistir.
 */
export interface BaridLocalAgentTicketIssueResponse {
  ticket: string;
  /** ISO 8601. */
  expiresAt: string;
  deviceId?: string;
  deviceSettingId?: string;
  kid?: string;
}

export const BARID_LOCAL_AGENT_TICKET_HEADER = 'X-Barid-Agent-Ticket';
/** A tela renova o ticket quando faltar isto para expirar (LOCAL_AGENT_TICKET_RENEW_BEFORE_SECONDS). */
export const BARID_LOCAL_AGENT_TICKET_RENEW_BEFORE_MS = 60 * 60 * 1000;

/** Codigos de recusa do agente no caminho do ticket (corpo `{ error, code }` ou reason do close 4401 do WS). */
export type BaridLocalAgentTicketRejectionCode =
  | 'SESSION_UNVERIFIED'
  | 'TICKET_INVALID'
  | 'TICKET_EXPIRED'
  | 'TICKET_REVOKED'
  | 'TENANT_MISMATCH'
  | 'DEVICE_MISMATCH'
  | 'ORIGIN_NOT_ALLOWED'
  | 'TICKET_RATE_LIMITED';

export const BARID_LOCAL_AGENT_TICKET_REJECTION_CODES: readonly BaridLocalAgentTicketRejectionCode[] = [
  'SESSION_UNVERIFIED',
  'TICKET_INVALID',
  'TICKET_EXPIRED',
  'TICKET_REVOKED',
  'TENANT_MISMATCH',
  'DEVICE_MISMATCH',
  'ORIGIN_NOT_ALLOWED',
  'TICKET_RATE_LIMITED'
];

/** Recusas que um ticket NOVO resolve (pedir outro a nuvem e tentar de novo uma vez). */
export const BARID_LOCAL_AGENT_TICKET_RENEWABLE_CODES: readonly BaridLocalAgentTicketRejectionCode[] = [
  'TICKET_INVALID',
  'TICKET_EXPIRED',
  'TICKET_REVOKED',
  'DEVICE_MISMATCH'
];

/** Mensagens (pt-BR) quando o agente recusa o ticket e nao mandou texto. */
export const BARID_LOCAL_AGENT_TICKET_MESSAGES: Record<BaridLocalAgentTicketRejectionCode, string> = {
  SESSION_UNVERIFIED:
    'O Barid ainda não conseguiu confirmar o acesso deste navegador. Verifique a internet do computador e tente novamente em alguns segundos.',
  TICKET_INVALID: 'O acesso deste navegador ao Barid é inválido. Atualize a página para renová-lo.',
  TICKET_EXPIRED: 'O acesso deste navegador ao Barid expirou. Atualize a página para renová-lo.',
  TICKET_REVOKED: 'O acesso deste navegador ao Barid foi encerrado. Entre novamente no Wazir.',
  TENANT_MISMATCH: 'O Barid deste computador está pareado com outra empresa. Entre no Wazir com a empresa deste computador.',
  DEVICE_MISMATCH: 'Este acesso foi emitido para outro computador. Atualize a página para renová-lo.',
  ORIGIN_NOT_ALLOWED: 'O Barid deste computador não aceita acessos deste endereço do Wazir.',
  TICKET_RATE_LIMITED: 'Muitas tentativas de acesso inválidas ao Barid. Aguarde um minuto e tente novamente.'
};

export function isBaridTicketRejectionCode(code: unknown): code is BaridLocalAgentTicketRejectionCode {
  return typeof code === 'string' && (BARID_LOCAL_AGENT_TICKET_REJECTION_CODES as readonly string[]).includes(code);
}

/** Eventos do WS loopback `/events` (LOCAL_AGENT_EVENTS do contrato). */
export const BARID_AGENT_EVENTS = [
  'agent.connected',
  'agent.disconnected',
  'print.completed',
  'print.failed',
  'printer.discovered',
  'printers.synced',
  'tef.pending',
  'tef.completed',
  'tef.failed',
  'fiscal.completed',
  'fiscal.failed',
  'edge.fiscal.pending.alert',
  'edge.print.job.alert',
  'edge.outbox.op.quarantined'
] as const;

export type BaridAgentEventName = (typeof BARID_AGENT_EVENTS)[number];

/** ILocalAgentEvent do contrato. `agent.connected` traz o status do agente em `data`. */
export interface BaridAgentEvent<TData = unknown> {
  event: BaridAgentEventName;
  requestId?: string;
  timestamp: string;
  data?: TData;
}

/** 1a mensagem do cliente no `/events`: Bearer do pareamento OU ticket (contrato 1.30.0). */
export type BaridEventsAuthRequest = { type: 'AUTH'; authorization: string } | { type: 'AUTH'; ticket: string };

/** Close code do `/events` quando a autenticacao falha (reason = codigo). */
export const BARID_EVENTS_AUTH_CLOSE_CODE = 4401;

/** Resposta do `POST /panel/ticket` do agente (ticket de uso unico, 60 s). */
export interface BaridPanelTicketResult {
  ticket: string;
  expiresAt: string;
  url: string;
}
